<script setup lang="ts">
import { Head, useForm } from "@inertiajs/vue3"
import { router } from "@inertiajs/vue3"
import { useDebounceFn } from "@vueuse/core"
import { ChevronDownIcon, DownloadIcon, SearchIcon } from "lucide-vue-next"
import { ref, watch } from "vue"

import Heading from "@/components/Heading.vue"
import Badge from "@/components/custom/Badge.vue"
import InputDebounced from "@/components/custom/InputDebounced.vue"
import Pagination from "@/components/custom/Pagination.vue"
import Table from "@/components/custom/Table.vue"
import InsuranceExtApproveDialog from "@/components/manager/InsuranceExtApproveDialog.vue"
import InsuranceFamilyMemberApproveDialog from "@/components/manager/InsuranceFamilyMemberApproveDialog.vue"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { InputGroup, InputGroupAddon } from "@/components/ui/input-group"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import AppLayout from "@/layouts/AppLayout.vue"
import { dateFormatter } from "@/lib/formatters"
import { download } from "@/lib/utils"
import {
  exportExtensionManagerInsurancesPath,
  exportFamilyManagerInsurancesPath,
  managerInsurancesPath,
} from "@/routes"
import type { BreadcrumbItem } from "@/types"
import type {
  ManagerInsurance,
  ManagerInsurancesIndex,
} from "@/types/serializers"
import type { Column } from "@/types/table"

const props = defineProps<ManagerInsurancesIndex>()

const formFilters = useForm<{
  search?: string
  status?: string
  sort_by?: string
  order?: string
}>({
  search: props.query.search || undefined,
  status: props.query.status || undefined,
  sort_by: props.query.sort_by || undefined,
  order: props.query.order || undefined,
})

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Seguro de Salud",
    href: managerInsurancesPath(),
  },
]

const statusItems = [
  { value: null, label: "Todos los estados" },
  { value: "pending", label: "EN REVISIÓN" },
  { value: "approved", label: "APROBADO" },
  { value: "rejected", label: "RECHAZADO" },
]

const columns: Array<Column<ManagerInsurance>> = [
  {
    key: "id",
    header: "ID",
    sortable: true,
  },
  {
    key: "user_full_name",
    header: "Empleado",
    sortable: true,
  },
  {
    key: "type",
    header: "Tipo",
    sortable: true,
  },
  {
    key: "status",
    header: "Estado",
    sortable: true,
  },
  {
    key: "created_at",
    header: "Fecha de creación",
    right: true,
    sortable: true,
  },
  {
    id: "action",
    key: "id",
    right: true,
    header: "Acción",
    thClass: "w-fit",
    tdClass: "w-fit",
  },
]

const exportingType = ref<"family" | "extension" | null>(null)

async function exportInsurances(type: "family" | "extension") {
  if (exportingType.value) return

  const XCSRFToken = document
    .querySelector('meta[name="csrf-token"]')
    ?.getAttribute("content")

  if (!XCSRFToken) {
    console.error("CSRF token not found")
    return
  }

  const exportPath =
    type === "family"
      ? exportFamilyManagerInsurancesPath()
      : exportExtensionManagerInsurancesPath()

  try {
    exportingType.value = type

    const response = await fetch(exportPath, {
      method: "POST",
      headers: { "X-CSRF-Token": XCSRFToken },
    })

    if (!response.ok) throw new Error("Export failed")

    const blob = await response.blob()
    const filename =
      response.headers
        .get("Content-Disposition")
        ?.match(/filename="(.+)"/)?.[1] || `insurances-${Date.now()}.xlsx`

    download(blob, filename)
  } catch (error) {
    console.error("Export error:", error)
  } finally {
    exportingType.value = null
  }
}

function refreshData() {
  router.get(
    managerInsurancesPath(),
    { ...props.query, ...formFilters.data(), page: undefined },
    { preserveState: true, preserveScroll: true },
  )
}

const debouncedRefreshData = useDebounceFn(refreshData, 100)

function isFamilyInsuranceType(type: string) {
  return (
    type === "InsuranceFamilyActivate" || type === "InsuranceFamilyDeactivate"
  )
}

watch(formFilters, debouncedRefreshData)
</script>

<template>
  <Head :title="breadcrumbs[breadcrumbs.length - 1].title" />

  <AppLayout :breadcrumbs="breadcrumbs">
    <div class="px-6 py-4">
      <Heading
        title="Seguro de Salud (PCF)"
        description="Gestiona reembolsos de seguros"
      />

      <div class="mt-4 flex items-center gap-2">
        <InputGroup class="w-88">
          <InputDebounced
            :debounce="400"
            group
            placeholder="Buscar por nombre, equipo o departamento..."
            v-model="formFilters.search"
          />
          <InputGroupAddon>
            <SearchIcon />
          </InputGroupAddon>
        </InputGroup>

        <Select v-model="formFilters.status">
          <SelectTrigger class="w-44">
            <SelectValue placeholder="Todos los estados" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem
              v-for="item in statusItems"
              :key="item.label"
              :value="item.value"
            >
              {{ item.label }}
            </SelectItem>
          </SelectContent>
        </Select>

        <DropdownMenu>
          <DropdownMenuTrigger as-child>
            <Button
              variant="outline"
              class="ml-auto"
              :disabled="!!exportingType"
            >
              <DownloadIcon />
              {{ exportingType ? "Exportando..." : "Exportar" }}
              <ChevronDownIcon />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" class="w-56">
            <DropdownMenuItem
              :disabled="!!exportingType"
              @click="exportInsurances('family')"
            >
              Exportar seguro familiar
            </DropdownMenuItem>
            <DropdownMenuItem
              :disabled="!!exportingType"
              @click="exportInsurances('extension')"
            >
              Exportar extensión
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>

    <Table
      :data="records"
      :columns="columns"
      v-model:sort-by="formFilters.sort_by"
      v-model:order="formFilters.order"
    >
      <template #user_full_name-cell="{ row }">
        {{ row.user.full_name }}
      </template>

      <template #status-cell="{ row }">
        <Badge :title="row.reject_reason" class="w-fit" :status="row.status" />
      </template>

      <template #created_at-cell="{ row }">
        {{ dateFormatter(row.created_at, {}, "dateTime") }}
      </template>

      <template #action-cell="{ row }">
        <InsuranceFamilyMemberApproveDialog
          v-if="isFamilyInsuranceType(row.type)"
          :data="row"
        />
        <InsuranceExtApproveDialog v-else :data="row" />
      </template>

      <template #empty>No hay solicitudes</template>
    </Table>

    <Pagination class="my-4 mt-auto px-6" />
  </AppLayout>
</template>
